/**
 * The typed contract of the globals SDX installs on `Drupal`.
 *
 * `Drupal.sdxNavigation` (sdx_router), `Drupal.sdxWebSocket`
 * (sdx_websocket) and the `Drupal.sdx` namespace are declared once here
 * and merged into the global `DrupalGlobal` interface, so core stores,
 * framework hooks and templates read them typed instead of re-declaring
 * ad-hoc shapes and casting. Import via `@sdx/core/contracts`.
 */

/** Navigation transition status. */
export type SdxNavigationStatus = 'idle' | 'loading' | 'error';

/** The state every navigation subscriber receives. */
export interface SdxNavigationState {
  status: SdxNavigationStatus;
  url: string;
  path: string;
  scope: string;
  pageData: unknown;
  error: string | null;
}

/** Options accepted by navigate(). */
export interface SdxNavigateOptions {
  /** Default true. Push a new history entry when set. */
  pushState?: boolean;
  /** Force a specific scope, bypassing the classifier. */
  scope?: string;
  /** Replace the current history entry instead of pushing. */
  replace?: boolean;
}

/** Resolves a component id to its bundle URL for preloading. */
export type SdxComponentResolver = (componentId: string) => string | null;

/**
 * Public API of the SPA navigation engine (`Drupal.sdxNavigation`).
 *
 * `TState` lets the installer (sdx_router) expose its richer state type
 * while consumers depend only on the shape above.
 */
export interface SdxNavigationApi<TState extends SdxNavigationState = SdxNavigationState> {
  subscribe(listener: (state: Readonly<TState>) => void): () => void;
  getState(): Readonly<TState>;
  navigate(url: string, options?: SdxNavigateOptions): Promise<Readonly<TState>>;
  prefetch(url: string): void;
  abort(): void;
  isNavigating(): boolean;
  ready: Promise<void>;
  registerComponentResolver(resolver: SdxComponentResolver): () => void;
}

/** A message delivered on a websocket channel. */
export interface SdxChannelMessage {
  type: string;
  channel?: string;
  data?: unknown;
  [key: string]: unknown;
}

/** Public API of the websocket client (`Drupal.sdxWebSocket`). */
export interface SdxWebSocketClient {
  subscribe(channel: string, callback: (msg: SdxChannelMessage) => void): void;
  unsubscribe(channel: string, callback: (msg: SdxChannelMessage) => void): void;
  send(channel: string, data: unknown): void;
  isConnected(): boolean;
  onStateChange(callback: (connected: boolean) => void): void;
  offStateChange(callback: (connected: boolean) => void): void;
}

/** The `Drupal.sdx` shared-state namespace. */
export interface SdxNamespace {
  framework?: string;
  mountedRoots?: Map<Element, unknown> | Set<Element>;
  loadedComponents?: Set<string> | Map<string, unknown>;
  /**
   * Drops the mount coordinator's registry entry for the given container
   * and clears its `data-sdx-mounted` flag WITHOUT calling the framework
   * adapter's `unmount()`. Installed by the core behavior installer; every
   * framework adapter chains its own invalidation onto the handler, so
   * calling it from outside resets every framework's view of the
   * container in one call and the next `attachBehaviors` pass re-mounts.
   */
  resetMount?: (container: HTMLElement) => void;
  /** Dialog bridge: renders a structured node tree into an element. */
  renderStructured?: (element: HTMLElement, nodes: unknown[]) => void;
  /** Dialog bridge: tears down what renderStructured() rendered. */
  unmountStructured?: (element: HTMLElement) => void;
  [key: string]: unknown;
}

declare global {
  interface DrupalGlobal {
    /** SDX SPA navigation engine (installed by sdx_router/sdx-navigate.js). */
    sdxNavigation?: SdxNavigationApi;
    /** SDX websocket client (installed by sdx_websocket/sdx-websocket.js). */
    sdxWebSocket?: SdxWebSocketClient;
    /** SDX ecosystem shared state namespace. */
    sdx?: SdxNamespace;
  }
}

/** The navigation engine when installed (SSR-safe: NULL without a window). */
export function getNavigation(): SdxNavigationApi | null {
  if (typeof window === 'undefined') return null;
  return window.Drupal?.sdxNavigation ?? null;
}

/** The websocket client when installed (SSR-safe). */
export function getWebSocketClient(): SdxWebSocketClient | null {
  if (typeof window === 'undefined') return null;
  return window.Drupal?.sdxWebSocket ?? null;
}

/**
 * Hands a component URL resolver to the navigation engine.
 *
 * The engine (sdx_router/navigate) may load after a framework mount
 * script; when it is not there yet the registration is retried on
 * DOMContentLoaded. Replaces the copy every framework mount entry used
 * to carry.
 */
export function registerNavigationComponentResolver(resolver: SdxComponentResolver): void {
  const now = getNavigation();
  if (now) {
    now.registerComponentResolver(resolver);
    return;
  }
  if (typeof document === 'undefined') return;
  document.addEventListener('DOMContentLoaded', () => {
    getNavigation()?.registerComponentResolver(resolver);
  }, { once: true });
}
